import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card, Tabs, Descriptions, Tag, Badge, Space, Button, Typography,
  Progress, Form, Input, Select, InputNumber, message, Drawer,
  Skeleton, Row, Col, Statistic, Alert, Divider,
} from "antd";
import {
  ArrowLeftOutlined, EditOutlined, SaveOutlined,
  ProjectOutlined, DollarOutlined, CheckSquareOutlined,
} from "@ant-design/icons";
import { proyectosApi, tareasApi } from "../api/client";
import type { Proyecto, BusinessCase, Tarea } from "../types";
import { RAG_COLORS } from "../types";
import dayjs from "dayjs";

const { Title, Text } = Typography;
const { Option } = Select;

// ── RAG label ─────────────────────────────────────────────────────────────────
const RAGTag = ({ value }: { value: string }) => {
  const colors: Record<string, string> = { Verde: "#52c41a", Ámbar: "#faad14", Rojo: "#ff4d4f" };
  return (
    <Tag color={colors[value] ?? "default"} style={{ fontWeight: 600 }}>
      {value}
    </Tag>
  );
};

// ── Edit Estado Drawer ────────────────────────────────────────────────────────
function EditarProyectoDrawer({
  proyecto, open, onClose, onSuccess,
}: {
  proyecto: Proyecto; open: boolean; onClose: () => void; onSuccess: () => void;
}) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) form.setFieldsValue({
      fase: proyecto.fase,
      estado: proyecto.estado,
      porcentaje_avance: proyecto.porcentaje_avance,
      prioridad: proyecto.prioridad,
      bloqueantes: proyecto.bloqueantes,
      notas_proximos_pasos: proyecto.notas_proximos_pasos,
      tiene_ia: proyecto.tiene_ia,
      estado_cai: proyecto.estado_cai,
    });
  }, [open, proyecto, form]);

  const handleSubmit = async () => {
    const values = await form.validateFields();
    setLoading(true);
    try {
      await proyectosApi.actualizar(proyecto.id, values);
      message.success("Proyecto actualizado — RAG recalculado");
      onSuccess();
      onClose();
    } catch (err: any) {
      message.error(err.response?.data?.detail ?? "Error al actualizar");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Drawer
      title={<Space><EditOutlined style={{ color: "#1a1f5e" }} /><span>Actualizar proyecto</span></Space>}
      width={520}
      open={open}
      onClose={onClose}
      footer={
        <Space style={{ float: "right" }}>
          <Button onClick={onClose}>Cancelar</Button>
          <Button type="primary" icon={<SaveOutlined />} loading={loading}
            onClick={handleSubmit} style={{ background: "#1a1f5e" }}>
            Guardar cambios
          </Button>
        </Space>
      }
    >
      <Form form={form} layout="vertical" requiredMark="optional">
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item name="fase" label="Fase">
              <Select size="small">
                {["F0","F1","Triage CAI","F2","F3","F4"].map((f) => <Option key={f} value={f}>{f}</Option>)}
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="estado" label="Estado">
              <Select size="small">
                {["Scoping","Estimating","Approving","Scheduling","Developing","UAT","Deploying","Finished","Cancelled"]
                  .map((e) => <Option key={e} value={e}>{e}</Option>)}
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="prioridad" label="Prioridad">
              <Select size="small">
                {["Urgente","Alta","Media","Baja"].map((p) => <Option key={p} value={p}>{p}</Option>)}
              </Select>
            </Form.Item>
          </Col>
        </Row>
        <Form.Item name="porcentaje_avance" label="% Avance">
          <InputNumber min={0} max={100} suffix="%" style={{ width: "100%" }} />
        </Form.Item>
        <Form.Item name="bloqueantes" label="Bloqueantes (si los hay)">
          <Input.TextArea rows={2} placeholder="Describe el bloqueante actual..." />
        </Form.Item>
        <Form.Item name="notas_proximos_pasos" label="Próximos pasos">
          <Input.TextArea rows={3} placeholder="Acciones para la próxima semana..." />
        </Form.Item>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="tiene_ia" label="¿Tiene IA?">
              <Select>
                <Option value={true}>Sí</Option>
                <Option value={false}>No</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="estado_cai" label="Estado CAI">
              <Select>
                {["Pendiente","En triage","Aprobado","Condicionado","Rechazado","N/A"]
                  .map((s) => <Option key={s} value={s}>{s}</Option>)}
              </Select>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Drawer>
  );
}

// ── Business Case Tab ─────────────────────────────────────────────────────────
function BusinessCaseTab({ proyectoId, bc }: { proyectoId: string; bc?: BusinessCase }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => { if (bc) form.setFieldsValue(bc); }, [bc, form]);

  const handleSave = async () => {
    const values = await form.validateFields();
    setLoading(true);
    try {
      await proyectosApi.upsertBC(proyectoId, values);
      message.success("Business Case guardado — ROI y Payback calculados automáticamente");
      setSaved(true);
    } catch {
      message.error("Error guardando Business Case");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {bc && (
        <Row gutter={16} style={{ marginBottom: 24 }}>
          {[
            { label: "ROI", value: bc.roi_porcentaje != null ? `${bc.roi_porcentaje}%` : "—", color: "#52c41a" },
            { label: "Payback", value: bc.payback_meses != null ? `${bc.payback_meses} meses` : "—", color: "#1a1f5e" },
            { label: "Beneficio neto anual", value: bc.beneficio_neto != null ? `€${bc.beneficio_neto?.toLocaleString()}` : "—", color: "#1a1f5e" },
          ].map(({ label, value, color }) => (
            <Col key={label} xs={24} sm={8}>
              <Card size="small" style={{ textAlign: "center", borderTop: `2px solid ${color}` }}>
                <Statistic title={label} value={value} valueStyle={{ color, fontSize: 22 }} />
              </Card>
            </Col>
          ))}
        </Row>
      )}

      <Form form={form} layout="vertical">
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="tipo_retorno" label="Tipo de retorno">
              <Select allowClear>
                {["Ingreso nuevo","Renovación","Ahorro operativo","Reducción AHT","Reducción FTE",
                  "Mejora calidad","Eficiencia interna","Cualitativo"].map((t) => <Option key={t} value={t}>{t}</Option>)}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="objetivo_estrategico" label="Objetivo estratégico">
              <Select allowClear>
                {["Eficiencia Operativa","Ingreso Digital","Innovación","Reducción de coste","Mejora CX"]
                  .map((o) => <Option key={o} value={o}>{o}</Option>)}
              </Select>
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="coste_estimado_implementacion" label="Coste estimado (€)">
              <InputNumber style={{ width: "100%" }} formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")} min={0} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="beneficio_mensual_estimado" label="Beneficio mensual estimado (€)">
              <InputNumber style={{ width: "100%" }} formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")} min={0} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="beneficio_anual_estimado" label="Beneficio anual estimado (€)">
              <InputNumber style={{ width: "100%" }} formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")} min={0} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="nivel_confianza" label="Nivel de confianza">
              <Select allowClear>
                <Option value="Bajo">Bajo</Option>
                <Option value="Medio">Medio</Option>
                <Option value="Alto">Alto</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>
        <Button type="primary" icon={<SaveOutlined />} loading={loading}
          onClick={handleSave} style={{ background: "#1a1f5e" }}>
          Calcular ROI y Payback
        </Button>
      </Form>
    </div>
  );
}

// ── Main Detail Page ──────────────────────────────────────────────────────────
export default function ProyectoDetallePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [proyecto, setProyecto] = useState<Proyecto | null>(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);

  const cargar = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await proyectosApi.obtener(id);
      setProyecto(res.data);
    } catch {
      message.error("Proyecto no encontrado");
      navigate("/proyectos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { cargar(); }, [id]);

  if (loading || !proyecto) {
    return <Skeleton active paragraph={{ rows: 8 }} />;
  }

  const diasSinActualizar = proyecto.fecha_ultimo_cambio
    ? dayjs().diff(dayjs(proyecto.fecha_ultimo_cambio), "day")
    : null;

  return (
    <div>
      {/* Header */}
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate("/proyectos")} type="text" />
        <Text code style={{ fontSize: 13 }}>{proyecto.proj_id}</Text>
      </Space>

      <div style={{ marginBottom: 20, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <Title level={4} style={{ margin: 0, color: "#1a1f5e" }}>{proyecto.nombre}</Title>
          <Space size={8} style={{ marginTop: 6 }} wrap>
            <RAGTag value={proyecto.estado_rag} />
            <Tag color="blue">{proyecto.estado}</Tag>
            <Tag>{proyecto.fase}</Tag>
            <Tag color="orange">{proyecto.prioridad}</Tag>
            {proyecto.tiene_ia && <Tag color="purple">IA · {proyecto.estado_cai}</Tag>}
            {proyecto.tipo_tecnologia && <Tag color="geekblue">{proyecto.tipo_tecnologia}</Tag>}
          </Space>
        </div>
        <Button icon={<EditOutlined />} onClick={() => setEditOpen(true)}
          style={{ borderColor: "#1a1f5e", color: "#1a1f5e" }}>
          Actualizar
        </Button>
      </div>

      {/* Alertas */}
      {diasSinActualizar !== null && diasSinActualizar > 7 && (
        <Alert type="warning" showIcon message={`Sin actualizar hace ${diasSinActualizar} días — el RAG puede degradarse`} style={{ marginBottom: 16 }} />
      )}
      {proyecto.bloqueantes && (
        <Alert type="error" showIcon message={`Bloqueante: ${proyecto.bloqueantes}`} style={{ marginBottom: 16 }} />
      )}

      {/* Progreso */}
      <Card size="small" style={{ marginBottom: 16, borderLeft: `3px solid ${RAG_COLORS[proyecto.estado_rag]}` }}>
        <Row gutter={24} align="middle">
          <Col xs={24} md={12}>
            <Text style={{ fontSize: 12, color: "#888" }}>Avance del proyecto</Text>
            <Progress
              percent={proyecto.porcentaje_avance}
              strokeColor={RAG_COLORS[proyecto.estado_rag]}
              trailColor="#f0f0f0"
              style={{ marginTop: 4 }}
            />
          </Col>
          <Col xs={12} md={6}>
            <Statistic
              title="Inicio"
              value={proyecto.fecha_inicio ? dayjs(proyecto.fecha_inicio).format("DD/MM/YYYY") : "—"}
              valueStyle={{ fontSize: 14 }}
            />
          </Col>
          <Col xs={12} md={6}>
            <Statistic
              title="Fin previsto"
              value={proyecto.fecha_fin_prevista ? dayjs(proyecto.fecha_fin_prevista).format("DD/MM/YYYY") : "—"}
              valueStyle={{
                fontSize: 14,
                color: proyecto.fecha_fin_prevista && dayjs(proyecto.fecha_fin_prevista).diff(dayjs(), "day") < 14
                  ? "#ff4d4f" : undefined,
              }}
            />
          </Col>
        </Row>
      </Card>

      {/* Tabs */}
      <Card size="small">
        <Tabs
          items={[
            {
              key: "info",
              label: <Space><ProjectOutlined />Información</Space>,
              children: (
                <Descriptions column={{ xs: 1, sm: 2 }} size="small">
                  <Descriptions.Item label="Tipo">{proyecto.tipo}</Descriptions.Item>
                  <Descriptions.Item label="Línea">{proyecto.linea}</Descriptions.Item>
                  <Descriptions.Item label="Cliente / Área">{proyecto.cliente_area ?? "—"}</Descriptions.Item>
                  <Descriptions.Item label="Sponsor">{proyecto.sponsor ?? "—"}</Descriptions.Item>
                  <Descriptions.Item label="Vertical">{proyecto.vertical ?? "—"}</Descriptions.Item>
                  <Descriptions.Item label="Mercado">{proyecto.mercado ?? "—"}</Descriptions.Item>
                  {proyecto.notas_proximos_pasos && (
                    <Descriptions.Item label="Próximos pasos" span={2}>
                      {proyecto.notas_proximos_pasos}
                    </Descriptions.Item>
                  )}
                </Descriptions>
              ),
            },
            {
              key: "bc",
              label: <Space><DollarOutlined />Business Case</Space>,
              children: <BusinessCaseTab proyectoId={proyecto.id} bc={proyecto.business_case} />,
            },
            {
              key: "tareas",
              label: <Space><CheckSquareOutlined />Tareas</Space>,
              children: (
                <Text type="secondary">
                  Las tareas de este proyecto se gestionan desde el módulo de Tareas.
                </Text>
              ),
            },
          ]}
        />
      </Card>

      <EditarProyectoDrawer
        proyecto={proyecto}
        open={editOpen}
        onClose={() => setEditOpen(false)}
        onSuccess={cargar}
      />
    </div>
  );
}
